import { useEffect, useRef, useState } from "react";
import type { ReadingType } from "../Components/QuestionInput/QuestionInput";
import { deckData } from "../Services/deckData";
import type { SpreadType } from "../config/spreadConfigs";
import type { CardInfo, Phase } from "./types";
import { READING_TYPE_TO_PHASE } from "./types";

const MAX_CLARIFYING_CARDS = 5;

function findCardImage(cardName: string): string {
  return deckData.find((card) => card.cardName === cardName)?.image ?? "";
}

export function useReadingSession() {
  const [phase, setPhase] = useState<Phase>("question");
  const [question, setQuestion] = useState("");
  const [spreadType, setSpreadType] = useState<SpreadType | null>(null);
  const [isYesOrNo, setIsYesOrNo] = useState(false);
  const [drawnCard, setDrawnCard] = useState<CardInfo | null>(null);
  const [deckKey, setDeckKey] = useState(0);
  const [showClarifyingDeck, setShowClarifyingDeck] = useState(false);
  const [clarifyingCards, setClarifyingCards] = useState<CardInfo[]>([]);
  const [clarifyingDeckKey, setClarifyingDeckKey] = useState(0);
  const clarifyingDeckRef = useRef<HTMLDivElement>(null);

  const clarifyingActive = showClarifyingDeck || clarifyingCards.length > 0;
  const clarifyingLimitReached = clarifyingCards.length >= MAX_CLARIFYING_CARDS;
  const excludedCardNames = [
    ...(drawnCard ? [drawnCard.name] : []),
    ...clarifyingCards.map((card) => card.name),
  ];

  useEffect(() => {
    if (!showClarifyingDeck) return;
    const scrollTimer = setTimeout(() => {
      clarifyingDeckRef.current?.scrollIntoView({ behavior: "smooth", block: "center" });
    }, 300);
    return () => clearTimeout(scrollTimer);
  }, [showClarifyingDeck]);

  const handleQuestionSubmit = (submittedQuestion: string, readingType: ReadingType) => {
    setQuestion(submittedQuestion);
    setIsYesOrNo(readingType === "yes-or-no");
    setSpreadType(readingType === "single" || readingType === "yes-or-no" ? null : readingType);
    setPhase(READING_TYPE_TO_PHASE[readingType]);
  };

  const handleCardRevealed = (cardName: string, isReversed: boolean) => {
    setDrawnCard({ name: cardName, isReversed, image: findCardImage(cardName) });
    setPhase("reading");
  };

  const handleDrawClarifying = () => {
    setClarifyingDeckKey((previousKey) => previousKey + 1);
    setShowClarifyingDeck(true);
  };

  const handleClarifyingCardRevealed = (cardName: string, isReversed: boolean) => {
    setClarifyingCards((previousCards) => [
      ...previousCards,
      { name: cardName, isReversed, image: findCardImage(cardName) },
    ]);
    setShowClarifyingDeck(false);
  };

  const handleDrawAgain = () => {
    setDeckKey((previousKey) => previousKey + 1);
    setDrawnCard(null);
    setClarifyingCards([]);
    setShowClarifyingDeck(false);
    setQuestion("");
    setSpreadType(null);
    setIsYesOrNo(false);
    setPhase("question");
  };

  return {
    phase,
    question,
    spreadType,
    isYesOrNo,
    drawnCard,
    deckKey,
    showClarifyingDeck,
    clarifyingCards,
    clarifyingDeckKey,
    clarifyingDeckRef,
    clarifyingActive,
    clarifyingLimitReached,
    excludedCardNames,
    handleQuestionSubmit,
    handleCardRevealed,
    handleDrawClarifying,
    handleClarifyingCardRevealed,
    handleDrawAgain,
  };
}
